import React, { useEffect, useState } from 'react';
import { useNavbar } from '../../../hooks/use-navbar';

interface Contact {
  name: string;
  role: string;
  email: string | null;
  phone: string | null;
  initials: string;
  color: string;
}

// Eclaircit une couleur hex en la melangeant avec du blanc.
// Utilise pour creer la teinte claire du gradient sur les avatars.
function lighten(hex: string, amount: number): string {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const f = amount / 100;
  const nr = Math.round(r + (255 - r) * f);
  const ng = Math.round(g + (255 - g) * f);
  const nb = Math.round(b + (255 - b) * f);
  return `#${[nr, ng, nb].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

const MailIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <polyline points="22,7 12,14 2,7" />
  </svg>
);

const PhoneIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

interface Section {
  title: string;
  subtitle: string;
  contacts: Contact[];
}

const sections: Section[] = [
  {
    title: 'Direction générale',
    subtitle: 'Stratégie · Achats',
    contacts: [
      {
        name: 'Sébastien Zabner',
        role: 'Direction générale\nResponsable achats',
        email: 'camion.net.seb@wanadoo.fr',
        phone: '06 64 48 47 64',
        initials: 'SZ',
        color: '#1a6fa8',
      },
    ],
  },
  {
    title: 'Commercial',
    subtitle: 'Devis · Relation client',
    contacts: [
      {
        name: 'Fabien Buchheit',
        role: 'Responsable commercial',
        email: 'contact@camionnet.fr',
        phone: '06 80 93 61 31',
        initials: 'FB',
        color: '#0F7A92',
      },
    ],
  },
  {
    title: 'Administratif',
    subtitle: 'Facturation',
    contacts: [
      {
        name: 'Leylla Miard',
        role: 'Responsable administrative et comptabilité',
        email: 'leylla@wanadoo.fr',
        phone: '06 31 74 18 60',
        initials: 'LM',
        color: '#2a7a5c',
      },
    ],
  },
  {
    title: 'Exploitation',
    subtitle: 'Qualité France',
    contacts: [
      {
        name: 'Franck Doiteaux',
        role: 'Exploitation & Qualité France',
        email: 'exploitation@camionnet.fr',
        phone: '06 99 56 06 40',
        initials: 'FD',
        color: '#7a4a2a',
      },
    ],
  },
  {
    title: 'Centre de lavage',
    subtitle: 'Rungis',
    contacts: [
      {
        name: 'Marc Lavergne',
        role: 'Responsable centre de lavage Rungis',
        email: 'rungis@camionnet.fr',
        phone: '06 89 15 57 62',
        initials: 'ML',
        color: '#5a2a7a',
      },
    ],
  },
  {
    title: 'Équipe terrain',
    subtitle: 'Agents par zone',
    contacts: [
      {
        name: 'Fiston',
        role: 'Paris / IDF',
        email: null,
        phone: '06 89 11 23 10',
        initials: 'FI',
        color: '#1e5ba3',
      },
      {
        name: 'Nata',
        role: 'Paris / IDF',
        email: null,
        phone: '06 89 07 88 40',
        initials: 'NA',
        color: '#1e5ba3',
      },
      {
        name: 'Julien',
        role: 'Paris / IDF',
        email: null,
        phone: '06 89 06 03 02',
        initials: 'JU',
        color: '#1e5ba3',
      },
      {
        name: 'Maxim',
        role: 'Paris / IDF',
        email: null,
        phone: '06 89 13 95 78',
        initials: 'MA',
        color: '#1e5ba3',
      },
      {
        name: 'Stéphane / Damien',
        role: 'Langres',
        email: null,
        phone: '06 89 13 86 72',
        initials: 'SD',
        color: '#b54015',
      },
      {
        name: 'Mawloud',
        role: 'Lyon',
        email: null,
        phone: '06 89 07 75 43',
        initials: 'MW',
        color: '#c2540a',
      },
      {
        name: 'Romain',
        role: 'Metz / Nancy',
        email: null,
        phone: '06 89 12 32 16',
        initials: 'RO',
        color: '#2a6c5c',
      },
      {
        name: 'Matthias',
        role: 'Strasbourg / Mulhouse',
        email: null,
        phone: '06 31 77 96 46',
        initials: 'MT',
        color: '#4a3a8a',
      },
    ],
  },
];

export const Contact: React.FC = () => {
  const navbar = useNavbar();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (navbar) {
      navbar.setTitle('Nous contacter');
      navbar.setBackArrow(false);
      navbar.setRight();
    }
  }, [navbar]);

  const q = query.trim().toLowerCase();
  const filteredSections = sections
    .map((section) => ({
      ...section,
      contacts: q
        ? section.contacts.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            c.role.toLowerCase().includes(q) ||
            section.title.toLowerCase().includes(q),
        )
        : section.contacts,
    }))
    .filter((section) => section.contacts.length > 0);

  return (
    <div
      style={{
        background: 'var(--bg-base)',
        paddingBottom: 'calc(40px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      {/* Barre de recherche */}
      <div
        style={{
          padding: '12px 16px',
          background: 'var(--bg-base)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 12,
            padding: '10px 14px',
          }}
        >
          <span style={{ fontSize: 17, color: 'var(--text-muted)', flexShrink: 0 }}>🔍</span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Chercher un contact, un rôle, une zone…"
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              fontSize: 14,
              fontFamily: 'inherit',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Effacer"
              style={{
                appearance: 'none',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: 18,
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {filteredSections.length === 0 && (
        <div
          style={{
            padding: '40px 24px',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: 13,
          }}
        >
          Aucun contact ne correspond à « {query} »
        </div>
      )}

      {/* Sections */}
      {filteredSections.map((section) => (
        <div key={section.title} style={{ marginBottom: 8 }}>
          {/* Section header */}
          <div
            style={{
              padding: '14px 16px 6px',
              background: 'var(--bg-base)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--teal)',
              }}
            >
              {section.title}
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-muted)',
                marginTop: 2,
                fontWeight: 500,
              }}
            >
              {section.subtitle}
            </div>
          </div>

          {/* Liste de rows */}
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 12,
              margin: '0 12px',
              overflow: 'hidden',
            }}
          >
            {section.contacts.map((contact, idx) => {
              const hasPhone = !!contact.phone;
              const rowStyle: React.CSSProperties = {
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                borderTop: idx === 0 ? 'none' : '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                cursor: hasPhone ? 'pointer' : 'default',
                textDecoration: 'none',
                WebkitTapHighlightColor: 'rgba(49,220,200,0.12)',
              };
              const rowKey = `${contact.name}-${contact.phone || contact.email}`;
              const inner = (
                <>
                  {/* Avatar avec degrade */}
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${lighten(contact.color, 28)} 0%, ${contact.color} 100%)`,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                      letterSpacing: '0.04em',
                      flexShrink: 0,
                      boxShadow: `0 2px 6px ${contact.color}55, inset 0 1px 0 rgba(255,255,255,0.18)`,
                    }}
                  >
                    {contact.initials}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: 'var(--text-primary)',
                        letterSpacing: '-0.005em',
                        lineHeight: 1.25,
                      }}
                    >
                      {contact.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--text-muted)',
                        lineHeight: 1.35,
                        marginTop: 1,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {contact.role}
                    </div>
                    {contact.phone && (
                      <div
                        style={{
                          fontSize: 12.5,
                          color: 'var(--teal)',
                          marginTop: 3,
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {contact.phone}
                      </div>
                    )}
                  </div>

                  {/* Icones d'action */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    {contact.email && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          window.location.href = `mailto:${contact.email}`;
                        }}
                        aria-label={`Email ${contact.name}`}
                        style={{
                          appearance: 'none',
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background: 'rgba(127,127,127,0.12)',
                          border: '1px solid var(--border-subtle)',
                          color: 'var(--text-secondary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          padding: 0,
                          WebkitTapHighlightColor: 'transparent',
                        }}
                      >
                        <MailIcon />
                      </button>
                    )}
                    {contact.phone && (
                      <div
                        aria-hidden
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, rgba(49,220,200,0.20), rgba(15,189,173,0.12))',
                          border: '1px solid rgba(49,220,200,0.4)',
                          color: 'var(--teal)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 6px rgba(49,220,200,0.15)',
                        }}
                      >
                        <PhoneIcon />
                      </div>
                    )}
                  </div>
                </>
              );

              return (
                <div
                  key={rowKey}
                  role={hasPhone ? 'button' : undefined}
                  tabIndex={hasPhone ? 0 : undefined}
                  onClick={
                    hasPhone
                      ? () => {
                        window.location.href = `tel:${contact.phone!.replace(/\s/g, '')}`;
                      }
                      : undefined
                  }
                  style={rowStyle}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
